import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import type { ComponentProps, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { AddToWatchlistIcon } from '@sorare/core/src/atoms/icons/AddToWatchlistIcon';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { AddToWatchlistButton } from 'components/watchlists/AddToWatchlistButton';
import { RemoveFromWatchlistButton } from 'components/watchlists/RemoveFromWatchlistButton';
import { LazyFixtureChart } from 'shared/stats/LastScores/LazyFixtureChart';

import { BuyingFloorPricePlayerDetails } from './BuyingFloorPricePlayerDetails';
import { BuyingPlayerDetailsButton } from './BuyingPlayerDetailsButton';
import { PlayerInjuryReport } from './PlayerInjuryReport';
import { PlayerNewsUpdates } from './PlayerNewsUpdates';
import { PlayerProperties } from './PlayerProperties';
import { PlayerStats } from './PlayerStats';
import { PlayerUnavailabilityPanel } from './PlayerUnavailabilityPanel';
import { PlayerUpcomingGames } from './PlayerUpcomingGames';
import type {
  PlayerDetailsQuery,
  PlayerDetailsQueryVariables,
  PlayerDetails_playerGameScoreInterface,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  min-height: 100%;
`;
const Main = styled(FullWidthContainer)`
  padding-top: var(--container-padding);
  padding-bottom: var(--container-padding);
  flex-grow: 1;
`;
const Header = styled(SBHorizontal)`
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--c-black);
  padding: var(--double-unit) var(--double-unit) var(--double-unit) var(--unit);
  border-bottom: 1px solid var(--c-nd-150);
`;
const Content = styled(Vertical)`
  > *:not(:last-child) {
    padding-bottom: var(--triple-unit);
    border-bottom: 1px solid var(--c-nd-150);
  }
`;
const Footer = styled(Vertical).attrs({ gap: 2 })`
  ${regularMaterial}
  position: sticky;
  bottom: 0;
  padding: var(--double-unit);
  border-top: 1px solid var(--c-nd-150);
`;

const PLAYER_DETAILS_QUERY = gql`
  query PlayerDetailsQuery(
    $playerSlug: String!
    $cardSlug: String!
    $gameId: ID
    $so5LeaderboardSlug: String
    $shouldFetchCard: Boolean!
    $inSeason: Boolean
    $rarity: Rarity
    $withBuyingOptions: Boolean!
    $onlyPrimary: Boolean = false
  ) {
    anyPlayer(slug: $playerSlug) {
      slug
      activeClub {
        slug
        ...PlayerUpcomingGames_teamInterface
        upcomingAnyGames(first: 3) {
          id
          ...PlayerUpcomingGames_game
        }
      }
      activeNationalTeam {
        slug
        ...PlayerUpcomingGames_teamInterface
        upcomingAnyGames(first: 3) {
          id
          ...PlayerUpcomingGames_game
        }
      }
      news(last: 10) {
        date
      }
      ...PlayerProperties_anyPlayer
      ...PlayerUnavailabilityPanel_anyPlayer
      ...PlayerInjuryReport_anyPlayer
      ...PlayerNewsUpdates_anyPlayer
      ...BuyingPlayerDetailsButton_anyPlayer
      ...BuyingFloorPricePlayerDetails_anyPlayer
      ...RemoveFromWatchlistButton_anyPlayer
      ...AddToWatchlistButton_anyPlayer
      ...PlayerStats_anyPlayerInterface
    }
    anyCard(slug: $cardSlug) @include(if: $shouldFetchCard) {
      slug
      anyPositions
      eligiblePlayerGameScores(gameId: $gameId) {
        id
        ...PlayerStats_playerGameScoreInterface
      }
      ...PlayerProperties_anyCard
      ...PlayerStats_anyCardInterface
    }
  }
  ${PlayerUpcomingGames.fragments.game}
  ${PlayerUpcomingGames.fragments.team}
  ${PlayerProperties.fragments.anyPlayer}
  ${PlayerProperties.fragments.anyCard}
  ${PlayerUnavailabilityPanel.fragments.anyPlayer}
  ${PlayerInjuryReport.fragments.anyPlayer}
  ${PlayerNewsUpdates.fragments.anyPlayer}
  ${BuyingPlayerDetailsButton.fragments.anyPlayer}
  ${BuyingFloorPricePlayerDetails.fragments.anyPlayer}
  ${RemoveFromWatchlistButton.fragments.anyPlayer}
  ${AddToWatchlistButton.fragments.anyPlayer}
  ${PlayerStats.fragments.anyPlayerInterface}
  ${PlayerStats.fragments.anyCardInterface}
  ${PlayerStats.fragments.playerGameScoreInterface}
` as TypedDocumentNode<PlayerDetailsQuery, PlayerDetailsQueryVariables>;

type Props = {
  playerSlug: string;
  cardSlug?: string;
  gameId?: string;
  onClose?: () => void;
  extraActions?: ReactNode;
  so5LeaderboardSlug?: string;
  onScoreSelect?: ComponentProps<typeof LazyFixtureChart>['onScoreSelect'];
  withBuyingOptions?: {
    rarity: Rarity;
    inSeason: boolean;
    onMarketClick: (playerSlug: string) => void;
  };
  watchlistId?: string;
  favoritesWatchlist?: boolean;
  onboarding?: boolean;
  footer?: ReactNode;
};

export const PlayerDetails = ({
  playerSlug,
  cardSlug,
  gameId,
  so5LeaderboardSlug,
  onClose,
  extraActions,
  onScoreSelect,
  withBuyingOptions,
  watchlistId,
  favoritesWatchlist,
  onboarding,
  footer,
}: Props) => {
  const { formatMessage } = useIntl();

  const { data: playerDetailsData, loading: playerDetailsLoading } = useQuery(
    PLAYER_DETAILS_QUERY,
    {
      variables: {
        playerSlug,
        gameId,
        so5LeaderboardSlug,
        cardSlug: cardSlug || '',
        shouldFetchCard: !!cardSlug,
        inSeason: withBuyingOptions?.inSeason,
        rarity: withBuyingOptions?.rarity,
        withBuyingOptions: !!withBuyingOptions,
      },
    }
  );

  const { anyCard } = playerDetailsData || {};
  if (playerDetailsLoading || !playerDetailsData) {
    return (
      <Root>
        <LoadingIndicator fullHeight />
      </Root>
    );
  }

  const { activeClub, activeNationalTeam } = playerDetailsData?.anyPlayer || {};
  const upcomingGamesWithTeam = [activeClub, activeNationalTeam]
    .filter(Boolean)
    .flatMap(team =>
      team.upcomingAnyGames.filter(Boolean).map(game => ({ game, team }))
    );

  return (
    <Root>
      {!!onClose && (
        <Header>
          <SBHorizontal>
            <IconButton
              size="medium"
              onClick={onClose}
              icon={faChevronLeft}
              color="transparent"
              aria-label={formatMessage(glossary.close)}
            />
            {!!extraActions && <Horizontal>{extraActions}</Horizontal>}
          </SBHorizontal>
        </Header>
      )}
      <Main>
        <Content gap={3}>
          <Vertical gap={2}>
            <PlayerProperties
              usePlayerPicture
              useCardPositions
              card={anyCard}
              player={playerDetailsData.anyPlayer}
              disableLinks={onboarding}
            />
            <PlayerStats
              anyPlayer={playerDetailsData.anyPlayer}
              anyCard={anyCard}
              eligiblePlayerGameScores={
                playerDetailsData.anyCard?.eligiblePlayerGameScores || []
              }
            />
          </Vertical>
          <PlayerInjuryReport player={playerDetailsData.anyPlayer} />
          {!!playerDetailsData.anyPlayer.news && (
            <PlayerNewsUpdates player={playerDetailsData.anyPlayer} />
          )}
          <PlayerUnavailabilityPanel player={playerDetailsData.anyPlayer} />
          {!!withBuyingOptions && (
            <BuyingFloorPricePlayerDetails
              anyPlayer={playerDetailsData.anyPlayer}
              inSeason={withBuyingOptions.inSeason}
            />
          )}
          <Vertical gap={1.5}>
            <LazyFixtureChart
              playerSlug={playerDetailsData.anyPlayer.slug}
              onScoreSelect={onScoreSelect}
              disableLinks={onboarding}
              showUpsell={!onboarding}
            />
          </Vertical>
          <PlayerUpcomingGames gamesWithTeam={upcomingGamesWithTeam} />
        </Content>
      </Main>
      {!!(
        !onboarding &&
        (withBuyingOptions || !watchlistId || !favoritesWatchlist)
      ) && (
        <Footer>
          {footer || (
            <>
              {((!watchlistId && !favoritesWatchlist) ||
                !!withBuyingOptions) && (
                <Horizontal>
                  {!watchlistId && !favoritesWatchlist && (
                    <AddToWatchlistButton
                      size="medium"
                      color="tertiary"
                      startIcon={<AddToWatchlistIcon />}
                      anyPlayer={playerDetailsData.anyPlayer}
                      fullWidth={!withBuyingOptions}
                    />
                  )}
                  {!!withBuyingOptions && (
                    <BuyingPlayerDetailsButton
                      anyPlayer={playerDetailsData.anyPlayer}
                      rarity={withBuyingOptions.rarity}
                      inSeason={withBuyingOptions.inSeason}
                      onMarketClick={withBuyingOptions.onMarketClick}
                    />
                  )}
                </Horizontal>
              )}
              {(!!watchlistId || !!favoritesWatchlist) && (
                <Horizontal>
                  <RemoveFromWatchlistButton
                    anyPlayer={playerDetailsData.anyPlayer}
                    watchlistId={watchlistId}
                    favoritesWatchlist={favoritesWatchlist}
                    onClose={onClose}
                  />
                  <AddToWatchlistButton
                    size="medium"
                    color="tertiary"
                    fullWidth
                    startIcon={<AddToWatchlistIcon />}
                    anyPlayer={playerDetailsData.anyPlayer}
                  />
                </Horizontal>
              )}
            </>
          )}
        </Footer>
      )}
    </Root>
  );
};

PlayerDetails.fragments = {
  playerGameScore: gql`
    fragment PlayerDetails_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      ...PlayerStats_playerGameScoreInterface
    }
    ${PlayerStats.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<PlayerDetails_playerGameScoreInterface>,
};
